import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Paper,
  Group,
  Badge,
  Stack,
  Loader,
  Center,
  Table,
  ActionIcon,
  Tooltip,
  Select,
  Button,
  Modal,
  Progress,
  Box,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconEye,
  IconTrash,
  IconRefresh,
  IconCalendar,
  IconFilter,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { meetingsApi, type MeetingListItem, type MeetingStatus } from '@/services/meetings.api';
import { templatesApi } from '@/services/templates.api';
import type { DocumentTemplateListItem } from '@/types/template.types';
import { useAuthStore } from '@/stores/authStore';

export const MeetingsPage = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';

  const [meetings, setMeetings] = useState<MeetingListItem[]>([]);
  const [templates, setTemplates] = useState<DocumentTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [templateFilter, setTemplateFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Delete modal
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [meetingToDelete, setMeetingToDelete] = useState<MeetingListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        templateId: templateFilter ? parseInt(templateFilter) : undefined,
        status: statusFilter as MeetingStatus | undefined,
      };

      const [meetingsResponse, templatesData] = await Promise.all([
        isAdmin ? meetingsApi.getAllMeetings(filters) : meetingsApi.getMyMeetings(filters),
        templatesApi.getActiveForSelection(),
      ]);

      setMeetings(meetingsResponse.meetings);
      setTemplates(templatesData);
    } catch (err: any) {
      console.error('Error loading meetings:', err);
      setError(err.response?.data?.detail || 'Erro ao carregar reuniões');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, templateFilter, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle delete
  const handleDeleteClick = (meeting: MeetingListItem) => {
    setMeetingToDelete(meeting);
    openDeleteModal();
  };

  const handleConfirmDelete = async () => {
    if (!meetingToDelete) return;

    try {
      setDeleting(true);
      await meetingsApi.deleteMeeting(meetingToDelete.roomCode);

      notifications.show({
        title: 'Reunião excluída',
        message: 'A reunião foi excluída com sucesso',
        color: 'green',
      });

      closeDeleteModal();
      loadData();
    } catch (err: any) {
      console.error('Error deleting meeting:', err);
      notifications.show({
        title: 'Erro',
        message: err.response?.data?.detail || 'Erro ao excluir reunião',
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get status badge
  const getStatusBadge = (status: MeetingStatus) => {
    const config = {
      in_progress: { color: 'blue', label: 'Em andamento' },
      completed: { color: 'green', label: 'Concluída' },
      abandoned: { color: 'gray', label: 'Abandonada' },
    };
    const { color, label } = config[status];
    return <Badge color={color} variant="light">{label}</Badge>;
  };

  if (loading && meetings.length === 0) {
    return (
      <Center style={{ height: '60vh' }}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Carregando reuniões...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center style={{ height: '60vh' }}>
        <Stack align="center" gap="md">
          <Text size="xl" fw={600} c="red">
            {error}
          </Text>
          <Button onClick={loadData} leftSection={<IconRefresh size={16} />}>
            Tentar novamente
          </Button>
        </Stack>
      </Center>
    );
  }

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <Box>
          <Title order={2}>{isAdmin ? 'Todas as Reuniões' : 'Minhas Reuniões'}</Title>
          <Text c="dimmed" size="sm">
            {meetings.length} {meetings.length === 1 ? 'reunião encontrada' : 'reuniões encontradas'}
          </Text>
        </Box>

        <Group>
          <Button
            variant="subtle"
            leftSection={<IconRefresh size={16} />}
            onClick={loadData}
            loading={loading}
          >
            Atualizar
          </Button>
        </Group>
      </Group>

      {/* Filters */}
      <Paper p="md" mb="lg" withBorder>
        <Group gap="md">
          <IconFilter size={20} />
          <Select
            placeholder="Filtrar por template"
            data={templates.map((t) => ({ value: t.id.toString(), label: t.title }))}
            value={templateFilter}
            onChange={setTemplateFilter}
            clearable
            style={{ minWidth: 200 }}
          />
          <Select
            placeholder="Filtrar por status"
            data={[
              { value: 'in_progress', label: 'Em andamento' },
              { value: 'completed', label: 'Concluída' },
              { value: 'abandoned', label: 'Abandonada' },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            style={{ minWidth: 150 }}
          />
          {(templateFilter || statusFilter) && (
            <Button
              variant="subtle"
              size="sm"
              onClick={() => {
                setTemplateFilter(null);
                setStatusFilter(null);
              }}
            >
              Limpar filtros
            </Button>
          )}
        </Group>
      </Paper>

      {/* Table */}
      {meetings.length === 0 ? (
        <Paper p="xl" withBorder>
          <Center>
            <Stack align="center" gap="md">
              <IconCalendar size={48} color="var(--mantine-color-gray-5)" />
              <Text c="dimmed">Nenhuma reunião encontrada</Text>
            </Stack>
          </Center>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Sala</Table.Th>
                <Table.Th>Template</Table.Th>
                {isAdmin && <Table.Th>Analista</Table.Th>}
                <Table.Th>Data</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Preenchimento</Table.Th>
                <Table.Th style={{ width: 100 }}>Ações</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {meetings.map((meeting) => (
                <Table.Tr key={meeting.roomCode}>
                  <Table.Td>
                    <Badge variant="outline" color="gray">
                      {meeting.roomCode}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {meeting.templateTitle}
                    </Text>
                    <Text size="xs" c="dimmed">
                      v{meeting.templateVersion}
                    </Text>
                  </Table.Td>
                  {isAdmin && (
                    <Table.Td>
                      <Text size="sm">{meeting.ownerName}</Text>
                    </Table.Td>
                  )}
                  <Table.Td>
                    <Text size="sm">{formatDate(meeting.createdAt)}</Text>
                    {meeting.endedAt && (
                      <Text size="xs" c="dimmed">
                        Encerrada: {formatDate(meeting.endedAt)}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>{getStatusBadge(meeting.status)}</Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Progress
                        value={meeting.completionPercentage}
                        size="sm"
                        style={{ width: 80 }}
                        color={meeting.completionPercentage === 100 ? 'green' : 'blue'}
                      />
                      <Text size="xs" c="dimmed">
                        {meeting.filledFields}/{meeting.totalFields}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Tooltip label="Ver detalhes">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() => navigate(`/meetings/${meeting.roomCode}`)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Excluir">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleDeleteClick(meeting)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Delete Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={20} color="var(--mantine-color-red-6)" />
            <Text fw={600}>Excluir Reunião</Text>
          </Group>
        }
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Tem certeza que deseja excluir a reunião <strong>{meetingToDelete?.roomCode}</strong>?
          </Text>
          <Text size="sm" c="dimmed">
            Esta ação irá excluir permanentemente:
          </Text>
          <Box component="ul" style={{ margin: 0, paddingLeft: 20 }}>
            <li><Text size="sm" c="dimmed">Todos os campos preenchidos</Text></li>
            <li><Text size="sm" c="dimmed">O resumo da reunião</Text></li>
            <li><Text size="sm" c="dimmed">Toda a conversa (chat e transcrições)</Text></li>
          </Box>
          <Group justify="flex-end" gap="sm" mt="md">
            <Button variant="default" onClick={closeDeleteModal}>
              Cancelar
            </Button>
            <Button color="red" onClick={handleConfirmDelete} loading={deleting}>
              Excluir
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};
